import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { LayoutDashboard, Car, User, Plus, Bell, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

const LINKS = [
  { label: 'Overview', path: '/dashboard', icon: LayoutDashboard },
  { label: 'My Rides', path: '/dashboard/rides', icon: Car },
  { label: 'Book a Ride', path: '/dashboard/book', icon: Plus },
  { label: 'Profile', path: '/dashboard/profile', icon: User },
];

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:static md:flex md:flex-col`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="MDS Transportation" className="h-10 w-auto" />
          </Link>
          <button className="md:hidden" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {LINKS.map(({ label, path, icon: Icon }) => (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${pathname === path ? 'bg-purple-50 text-purple-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
              <Icon size={18} />{label}
            </Link>
          ))}
        </div>
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors w-full">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white h-16 px-4 flex items-center justify-between shadow-sm">
          <button className="md:hidden" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
          <h1 className="text-lg font-semibold text-gray-800 hidden md:block font-heading">My Dashboard</h1>
          <div className="flex items-center gap-3">
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Link to="/admin" className="text-sm text-purple-700 font-medium hover:underline">Admin Panel</Link>
            )}
            <Link to="/dashboard/book" className="btn-primary text-sm py-2 px-4 hidden sm:flex items-center gap-2">
              <Plus size={16} /> Book Ride
            </Link>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}
